import React from 'react';

import Badge from '@/components/Badge';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';

import {
  isChannelActorCapabilityForDisplay,
  isChannelActorEnabledForDisplay,
} from './operational';

const displayCompanyName = (company) =>
  company?.commercialName || company?.name || company?.legalName || null;

/** Company identity from the list/detail payload. Do not invent a value. */
export const resolveActorIdentity = (record) =>
  record?.company?.identity || record?.identity || null;

/** Commercial parent if the actor API already exposes it. Do not use ownerCompany. */
export const resolveCommercialParentName = (record) => {
  const parent =
    record?.parentCompany ||
    record?.registeredByCompany ||
    record?.commercialRelationship?.parentCompany ||
    null;
  const fromObject = displayCompanyName(parent);
  if (fromObject) return fromObject;
  return record?.parentCompanyName || record?.commercialRelationship?.parentCompanyName || null;
};

export const buildActorIdentityColumn = ({ formatMessage, highlight }) => ({
  key: 'company/identity',
  title: formatMessage({ id: 'global.text.identity' }),
  dataIndex: ['company', 'identity'],
  sorter: true,
  render: (_, record) => {
    const identity = resolveActorIdentity(record);
    if (!identity) return DEFAULT_EMPTY_VALUE;
    return highlight(identity, record);
  },
});

export const buildActorParentColumn = ({ formatMessage, highlight }) => ({
  key: 'parentCompany/commercialName',
  title: formatMessage({ id: 'global.text.commercialParent' }),
  dataIndex: ['parentCompany', 'commercialName'],
  sorter: false,
  render: (_, record) => {
    const name = resolveCommercialParentName(record);
    if (!name) return DEFAULT_EMPTY_VALUE;
    return highlight(name, record);
  },
});

export const renderActorEnabledBadge = (record, formatMessage) => {
  const enabled = isChannelActorEnabledForDisplay(record);
  return (
    <Badge
      status={enabled ? 'success' : 'default'}
      type="group-item"
      text={formatMessage({
        id: enabled ? 'global.text.enabled' : 'global.text.disabled',
      })}
    />
  );
};

export const renderActorCapabilityBadge = (record, field, formatMessage) => {
  const enabled = isChannelActorCapabilityForDisplay(record, field);
  return (
    <Badge
      status={enabled ? 'success' : 'default'}
      type="group-item"
      text={formatMessage({ id: enabled ? 'global.text.yes' : 'global.text.no' })}
    />
  );
};

export const mapActorEnabledDetailField = (row, item) => {
  const enabled = isChannelActorEnabledForDisplay(item);
  return {
    ...row,
    type: 'badge',
    badgeType: 'group-item',
    badgeStatus: enabled ? 'success' : 'default',
    value: enabled ? 'global.text.enabled' : 'global.text.disabled',
    locale: true,
  };
};

export const mapActorCapabilityDetailField = (row, item, field = row?.name) => {
  const enabled = isChannelActorCapabilityForDisplay(item, field);
  return {
    ...row,
    type: 'badge',
    badgeType: 'group-item',
    badgeStatus: enabled ? 'success' : 'default',
    value: enabled ? 'global.text.yes' : 'global.text.no',
    locale: true,
  };
};

/** Fill identity / parent labels in the read-only actor drawer. Pending/rejected are not an active ficha. */
export const mapActorDisplayDetailFields = (rows, item) => {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name === 'company.identity' || row.name === 'identity') {
      return { ...row, value: resolveActorIdentity(item) || DEFAULT_EMPTY_VALUE };
    }
    if (row.name === 'parentCompany.commercialName') {
      return { ...row, value: resolveCommercialParentName(item) || DEFAULT_EMPTY_VALUE };
    }
    if (row.name === 'isActive') {
      return mapActorEnabledDetailField(row, item);
    }
    return row;
  });
};
